import { UTMParams } from '../utils/utmCapture';
import { GamepadButton } from '@playcastdotio/MobileGamepad';

/**
 * Quickstart Theme Configuration
 * Defines visual theming for each game quickstart
 */

/**
 * Behavior when user exits fullscreen during active play
 */
export type FullscreenExitBehavior =
  | 'redirect_to_cta' // Immediately disconnect and show CTA/wishlist page
  | 'return_to_afk_with_overlay' // Show Play Now overlay again, restart AFK timer (default)
  | 'return_to_afk_silent' // Restart AFK timer without overlay, keep playing
  | 'continue_playing'; // Keep ad timer running, no interruption

/**
 * Button chaining configuration for mobile gamepad
 * Allows one button to automatically trigger another button first
 *
 * Example: In shooters, RT (fire) can chain to LT (aim) for easier mobile controls
 * Now strongly-typed using GamepadButton enum for type safety and autocomplete!
 */
export type ButtonChainRule = {
  /** The button that triggers the chain (e.g., GamepadButton.RT) */
  trigger: GamepadButton;

  /** The button that should be pressed first/simultaneously (e.g., GamepadButton.LT) */
  prerequisite: GamepadButton;

  /** Keep prerequisite held while trigger is held (default: true) */
  maintainWhileHeld?: boolean;

  /** Delay in ms between pressing prerequisite and pressing trigger (default: 0 = simultaneous) */
  chainDelayMs?: number;
};

/**
 * Timer position during gameplay
 * Simple top/bottom positioning for the countdown timer
 */
export type TimerPosition = 'top' | 'bottom';

/**
 * Mobile gamepad input configuration
 */
export type MobileGamepadConfig = {
  /** Button chaining rules (e.g., RT triggers LT first for ADS+Shoot) */
  buttonChains?: ButtonChainRule[];
};

export interface QuickstartTheme {
  slug: string;
  gameName: string;
  studioName: string;
  studioLogo?: string; // URL or path to studio logo
  thankYouStudioLogo?: string; // URL or path to studio logo specifically for thank you/exit pages (falls back to studioLogo)
  gameLogo?: string; // URL or path to game logo (used instead of gameName text if provided)
  pageBackgroundImage?: string; // URL or path to full-page background image
  primaryColor: string; // Hex color for buttons, accents
  secondaryColor: string; // Hex color for text, borders
  modalBackgroundColor: string; // Hex color for modal/card background
  modalBackgroundOpacity?: number; // Opacity for modal background (0-1), defaults to 0.95
  playNowModalBackgroundColor?: string; // Hex color for Play Now overlay modal background (falls back to modalBackgroundColor)
  playNowModalBackgroundOpacity?: number; // Opacity for Play Now overlay modal (falls back to modalBackgroundOpacity)
  releaseDate?: string; // Release date to display (e.g., "Jan 1st 2026") - if not set, badge not shown
  releaseDateTimestamp?: number; // Unix timestamp (milliseconds) of release date - used to automatically determine if game is released
  releaseDateTextColor?: string; // Text color for release date badge, defaults to modalBackgroundColor
  badgeOverrideTextColor?: string; // Text color for badge override (e.g., "SNEAK PEEK"), defaults to releaseDateTextColor
  spinnerColor?: string; // Color for loading spinner, defaults to primaryColor
  steamUrl?: string; // Wishlist URL (or store URL if game is released)
  storePlatform: string; // Name of the store platform (e.g., "Steam", "Epic Games Store", "GOG")
  discordUrl?: string; // Discord community URL
  wishlistButtonImage?: 'auto' | 'wishlist' | 'buy-on-steam'; // Which button image to use: 'auto' (default) uses release date logic
  timerPosition?: 'top' | 'bottom'; // Timer position during gameplay (default: 'bottom')
  inGameCtaText?: string; // Text for the in-game CTA button (e.g., "Wishlist!", "Get Game!", "Play Now!") - defaults to 'Wishlist!'
  afkTimeoutSeconds: number; // How long before AFK kick
  adTimerSeconds: number; // Total playtime allowed
  afkTimerBehavior?: 'reset' | 'cumulative'; // Reset timer on each Play Now page visit, or count cumulatively (default: reset)
  preferredAuthProvider?: 'discord' | 'facebook' | 'google' | 'linkedin' | 'microsoft' | 'tiktok' | 'twitch' | 'twitter'; // Preferred auth provider (fallback if UTM source doesn't match)
  fullscreenExitBehavior?: FullscreenExitBehavior; // What happens when user exits fullscreen (default: 'return_to_afk_with_overlay')
  requireAuth?: boolean; // Whether to require Clerk authentication after demo ends (default: false)
  sendWishlistEmail?: boolean; // Whether to send wishlist reminder email on thank you page (default: true)
  mobileGamepadConfig?: MobileGamepadConfig; // Mobile gamepad configuration (button chains, etc.)
}

/**
 * Hardcoded theme configurations
 * In the future, this will be fetched from an API
 *
 * Release Date Handling:
 * - releaseDate: Human-readable display text (e.g., "Oct 24th 2025")
 * - releaseDateTimestamp: Unix timestamp in milliseconds for automatic release detection
 * - If current time >= releaseDateTimestamp, game is considered "released"
 * - Released games show "Check it out now!" instead of "Wishlist on Steam"
 * - Released games hide the "COMING:" prefix on the release date
 */
export const QUICKSTART_THEMES: Record<string, QuickstartTheme> = {
  // Example test theme
  test: {
    slug: 'test',
    gameName: 'Test Game',
    studioName: 'Test Studio',
    primaryColor: '#3B82F6', // Blue
    secondaryColor: '#60A5FA',
    modalBackgroundColor: '#1E293B',
    modalBackgroundOpacity: 0.95,
    releaseDate: 'Jan 1st 2026',
    releaseDateTimestamp: new Date('2026-01-01T00:00:00Z').getTime(), // Jan 1, 2026
    steamUrl: 'https://store.steampowered.com/app/test', // Test URL
    storePlatform: 'Steam',
    afkTimeoutSeconds: 60, // 1 min for testing
    adTimerSeconds: 120, // 2 min for testing
    afkTimerBehavior: 'reset',
    fullscreenExitBehavior: 'return_to_afk_with_overlay',
  },

  // Example real theme (to be replaced with actual data)
  mygame: {
    slug: 'mygame',
    gameName: 'My Awesome Game',
    studioName: 'DeadDrop Studios',
    studioLogo: '/quickstart/mygame/studio-logo.png',
    pageBackgroundImage: '/quickstart/mygame/background.jpg',
    primaryColor: '#10B981', // Emerald
    secondaryColor: '#34D399',
    modalBackgroundColor: '#0F172A',
    modalBackgroundOpacity: 0.95,
    releaseDate: 'March 2026',
    releaseDateTimestamp: new Date('2026-03-01T00:00:00Z').getTime(), // March 1, 2026
    steamUrl: 'https://store.steampowered.com/app/123456',
    storePlatform: 'Steam',
    afkTimeoutSeconds: 180, // 3 minutes
    adTimerSeconds: 300, // 5 minutes
    afkTimerBehavior: 'reset',
    preferredAuthProvider: 'google',
    fullscreenExitBehavior: 'return_to_afk_with_overlay',
  },
  // Shades
  shades: {
    slug: 'shades',
    gameName: 'Outbreak: Shades of Horror',
    studioName: 'DeadDrop Studios',
    studioLogo: '/quickstart/shades/ddslogo.png',
    thankYouStudioLogo: '/quickstart/shades/ddslogo2.png',
    gameLogo: '/quickstart/shades/gamelogo.png', // If you have a game logo
    pageBackgroundImage: '/quickstart/shades/shades.png',
    primaryColor: '#FBBF24',
    secondaryColor: '#34D399',
    modalBackgroundColor: '#000000',
    modalBackgroundOpacity: 0.7, // Lower opacity to show background image more
    playNowModalBackgroundColor: '#020617', // Custom background for Play Now overlay
    playNowModalBackgroundOpacity: 0.5,
    releaseDate: 'Oct 24th 2025',
    releaseDateTimestamp: new Date('2025-10-24T00:00:00Z').getTime(), // Oct 24, 2025
    releaseDateTextColor: '#F97316', // Orange color for release date text
    badgeOverrideTextColor: '#FEF3C7', // White color for "SNEAK PEEK" text
    spinnerColor: '#FBBF24', // Yellow/gold color for spinner
    steamUrl: 'https://store.steampowered.com/app/1932190/Outbreak_Shades_of_Horror/?utm_source=playcast_dropin',
    storePlatform: 'Steam',
    afkTimeoutSeconds: 180, // 3 minutes
    adTimerSeconds: 60 * 30, // 30 minutes
    afkTimerBehavior: 'reset',
    preferredAuthProvider: 'google',
    fullscreenExitBehavior: 'redirect_to_cta',
    discordUrl: 'https://discord.com/invite/F4kntyaVR7',
    sendWishlistEmail: true,
    mobileGamepadConfig: {
      buttonChains: [],
    },
  },
  quick: {
    slug: 'quick',
    gameName: 'Quick',
    studioName: 'Quick',
    studioLogo: '/quickstart/shades/ddslogo.png',
    thankYouStudioLogo: '/quickstart/shades/ddslogo2.png',
    gameLogo: '/quickstart/shades/gamelogo.png',
    pageBackgroundImage: '/quickstart/shades/quick.png',
    primaryColor: '#FBBF24',
    secondaryColor: '#34D399',
    modalBackgroundColor: '#000000',
    modalBackgroundOpacity: 0.7,
    playNowModalBackgroundColor: '#020617',
    playNowModalBackgroundOpacity: 0.5,
    releaseDate: 'never lmao',
    releaseDateTimestamp: new Date('2099-12-31T23:59:59Z').getTime(), // Far future for testing
    releaseDateTextColor: '#F97316',
    badgeOverrideTextColor: '#FEF3C7',
    spinnerColor: '#FBBF24',
    steamUrl: 'https://store.steampowered.com/app/1932190',
    storePlatform: 'Steam',
    afkTimeoutSeconds: 10, // 10 seconds
    adTimerSeconds: 30, // 30 seconds
    afkTimerBehavior: 'reset',
    preferredAuthProvider: 'google',
    fullscreenExitBehavior: 'redirect_to_cta',
    mobileGamepadConfig: {
      buttonChains: [],
    },
  },
};

/**
 * Get theme for a quickstart slug
 * Falls back to test theme if not found
 *
 * Supports tilde-separated custom tracking data (e.g., "test~campaign~variant")
 * - Only the first part before the tilde is used for theme lookup
 * - Additional parts can be used for custom UTM tracking
 */
export function getQuickstartTheme(slug: string): QuickstartTheme {
  // Split on tilde and take first part for theme lookup
  // This allows custom tracking data like: test~utm_source~utm_campaign
  const themeKey = slug.split('~')[0];
  return QUICKSTART_THEMES[themeKey] || QUICKSTART_THEMES.test;
}

/**
 * Determine auth provider based on UTM source
 * UTM source takes priority over theme preference
 *
 * Supported OAuth providers: discord, facebook, google, linkedin, microsoft, tiktok, twitch
 */
export function getAuthProviderFromUTM(utmData?: UTMParams, theme?: QuickstartTheme): string {
  // Determine from UTM source first
  if (utmData?.utm_source) {
    const source = utmData.utm_source.toLowerCase();

    // Discord
    if (source.includes('discord')) {
      return 'discord';
    }

    // Facebook
    if (source.includes('facebook') || source.includes('fb') || source.includes('meta')) {
      return 'facebook';
    }

    // LinkedIn
    if (source.includes('linkedin')) {
      return 'linkedin';
    }

    // Microsoft
    if (source.includes('microsoft') || source.includes('msft') || source.includes('azure')) {
      return 'microsoft';
    }

    // TikTok
    if (source.includes('tiktok')) {
      return 'tiktok';
    }

    // Twitch
    if (source.includes('twitch')) {
      return 'twitch';
    }

    // Twitter/X
    if (source.includes('twitter') || source.includes('x.com') || source === 'x') {
      return 'twitter';
    }

    // Instagram, Reddit, YouTube -> Default to Google
    // (These platforms don't have OAuth integrations or users likely have Google)
    if (source.includes('instagram') || source.includes('reddit') || source.includes('youtube') || source.includes('ig')) {
      console.log(`[Auth] utm_source="${source}" -> defaulting to Google (no OAuth integration)`);
      return 'google';
    }

    // Unknown source - log for debugging and fall through to theme fallback
    console.log(`[Auth] Unknown utm_source="${source}" -> checking theme fallback`);
  }

  // Fallback to theme's preferred provider
  if (theme?.preferredAuthProvider) {
    console.log(`[Auth] Using theme preferred provider: ${theme.preferredAuthProvider}`);
    return theme.preferredAuthProvider;
  }

  // Final fallback
  return 'google';
}

/**
 * Calculate the best text color (white or black) for a given background color
 * Uses relative luminance calculation for accessibility
 *
 * @param hexColor - Background color in hex format (e.g., '#3B82F6')
 * @returns '#FFFFFF' for dark backgrounds, '#000000' for light backgrounds
 */
export const getContrastTextColor = (hexColor: string): string => {
  // Guard: Handle undefined/null/empty values - default to white text
  if (!hexColor || typeof hexColor !== 'string') {
    console.warn('[getContrastTextColor] Invalid hex color provided:', hexColor);
    return '#FFFFFF';
  }

  // Remove # if present
  const hex = hexColor.replace('#', '');

  // Guard: Validate hex format (should be 3 or 6 characters)
  if (!/^[0-9A-Fa-f]{3}$|^[0-9A-Fa-f]{6}$/.test(hex)) {
    console.warn('[getContrastTextColor] Invalid hex format:', hexColor);
    return '#FFFFFF';
  }

  // Expand shorthand hex (e.g., '03F' -> '0033FF')
  const fullHex = hex.length === 3 
    ? hex.split('').map(char => char + char).join('')
    : hex;

  // Parse hex to RGB
  const r = parseInt(fullHex.substring(0, 2), 16);
  const g = parseInt(fullHex.substring(2, 4), 16);
  const b = parseInt(fullHex.substring(4, 6), 16);

  // Calculate relative luminance using WCAG formula
  // https://www.w3.org/TR/WCAG20/#relativeluminancedef
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // Return white for dark colors, black for light colors
  // Threshold of 0.5 provides good contrast in most cases
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
};

/**
 * Get the appropriate text color for a theme's primary color
 */
export const getThemePrimaryTextColor = (theme: QuickstartTheme): string => {
  return getContrastTextColor(theme.primaryColor);
};

/**
 * Check if a game is released based on its release date timestamp
 * @param theme - The quickstart theme
 * @param currentTime - Optional current time (defaults to Date.now()), useful for testing
 * @returns true if game is released, false if upcoming or no release date set
 */
export const isGameReleased = (theme: QuickstartTheme, currentTime?: number): boolean => {
  if (!theme.releaseDateTimestamp) {
    return false; // No release date means unreleased
  }

  const now = currentTime ?? Date.now();
  return now >= theme.releaseDateTimestamp;
};

/**
 * Get the wishlist button image path based on theme settings and release status
 * @param theme - The quickstart theme
 * @returns Object with src and alt for the wishlist/store button image
 */
export const getWishlistButtonImage = (
  theme: QuickstartTheme
): { src: string; alt: string } => {
  // Check for explicit wishlist button image setting
  let setting = theme.wishlistButtonImage;

  // Backward compatibility: convert old useWishlist boolean to new setting
  if (setting === undefined) {
    const useWishlist = (theme as QuickstartTheme & { useWishlist?: boolean }).useWishlist;
    setting = useWishlist === false ? 'buy-on-steam' : 'wishlist';
  }

  // Default to 'auto' if no setting
  setting = setting || 'auto';

  // Manual override
  if (setting === 'wishlist') {
    return { src: '/quickstart/wishlist.png', alt: 'Wishlist Now' };
  }
  if (setting === 'buy-on-steam') {
    return { src: '/quickstart/buyonsteam.png', alt: 'Buy on Steam' };
  }

  // Auto mode: use release date to determine
  // If no release date is set, default to wishlist
  if (theme.releaseDateTimestamp == null) {
    return { src: '/quickstart/wishlist.png', alt: 'Wishlist Now' };
  }

  // If game is released, show buy on steam
  if (isGameReleased(theme)) {
    return { src: '/quickstart/buyonsteam.png', alt: 'Buy on Steam' };
  }

  // Game has release date but not released yet, show wishlist
  return { src: '/quickstart/wishlist.png', alt: 'Wishlist Now' };
};
